import { isNil, isUndefined } from "lodash";
import { DateTime } from "luxon";
import ms from "ms";
import validate from "validate.js";
import { errorMessage } from "../utils";
import type {
  ArrayEqualsOptions,
  BaseOptions,
  ConditionalPresenceOptions,
  ExcludesMemberOptions,
  HostOptions,
  ValidDurationStringOptions,
  ValidSentryDsnOptions,
} from "./Constraints";

validate.extend(validate.validators.datetime, {
  // The value is guaranteed not to be null or undefined but otherwise it
  // could be anything.
  parse: (value: any) => {
    if (value instanceof DateTime) {
      return value.toMillis();
    } else if (typeof value === "string") {
      return DateTime.fromISO(value).toMillis();
    } else if (typeof value === "number") {
      return value;
    } else if (value instanceof Date) {
      return value.getTime();
    }

    return NaN;
  },
  // Input is a unix timestamp
  format: (value: number) => {
    return DateTime.fromMillis(value).toLocaleString(DateTime.DATETIME_FULL);
  },
});

validate.validators.validJSONString = (value: string | null, options: BaseOptions, key: string, attributes: Record<string, any>) => {
  value = attributes[key];
  if (!isNil(value) && value != "") {
    try {
      JSON.parse(value);
    } catch (error) {
      return options.message || `must be a parseable JSON string and is not. Parsing failed with "${errorMessage(error)}"`;
    }
  }
};

validate.validators.arrayEquals = (value: any[], options: ArrayEqualsOptions) => {
  if (!Array.isArray(value) || !Array.isArray(options.value)) {
    return options.message || "both values must be arrays";
  }

  if (value.length !== options.value.length) {
    return options.message || "arrays are of different lengths";
  }

  // Sort and compare arrays
  const sortedValue = [...value].sort();
  const sortedOptions = [...options.value].sort();

  for (let i = 0; i < sortedValue.length; i++) {
    if (sortedValue[i] !== sortedOptions[i]) {
      return options.message || "elements in the arrays do not match";
    }
  }
};

validate.validators.excludesMember = (value: any[], options: ExcludesMemberOptions) => {
  if (Array.isArray(value) && value.includes(options.member)) {
    return options.message || `cannot include ${options.member}`;
  }
};

/*
 * This validator checks if a field is present after passing a conditional check on the same attributes.
 * For example, you can use this as a conditional field check:
 *
 * keyToCheck: {
 *   conditionalPresence: {
 *         severity: ProblemSeverity.Error,
 *         condition: (attributes) => !!attributes.anotherKey,
 *         message: "must have an inverse field set",
 *   }
 * }
 *
 * In this case, keyToCheck will have a presence validation only if "anotherKey" is not null
 */
validate.validators.conditionalPresence = (
  value: string | undefined,
  options: ConditionalPresenceOptions,
  _key: string,
  attributes: Record<string, any>
) => {
  if (options.condition(attributes)) {
    return validate.validators.presence(value, options);
  }
};

/**
 * Validates that a property is a valid duration string according to the `ms` library
 */
validate.validators.validDurationString = (value: string | undefined, options: ValidDurationStringOptions) => {
  let milliseconds: number;
  // don't add to the problems with format complaints if the value isn't present
  if (!value || value.trim() == "") {
    return;
  }

  try {
    milliseconds = ms(value);
  } catch (error) {
    return options.message ?? "is not a valid duration string";
  }

  // truthy value was passed but didn't parse cleanly
  if (isUndefined(milliseconds)) {
    return options.message ?? "is not a valid duration string";
  }

  if (!isUndefined(options.minimumMilliseconds) && milliseconds <= options.minimumMilliseconds) {
    return `must be more than ${ms(options.minimumMilliseconds, { long: true })}`;
  }

  if (!isUndefined(options.maximumMilliseconds) && milliseconds > options.maximumMilliseconds) {
    return `must be no more than ${ms(options.maximumMilliseconds, { long: true })}`;
  }
};

/**
 * Validates that a property is a valid URL host
 */
validate.validators.host = (value: string | undefined | null, options: HostOptions) => {
  if (!isNil(value)) {
    return validate.validators.url(`https://${value}`, options);
  }
};

// from https://github.com/getsentry/sentry-javascript/blob/develop/packages/utils/src/dsn.ts
const DSN_REGEX = /^(?:(\w+):)\/\/(?:(\w+)(?::(\w+)?)?@)([\w.-]+)(?::(\d+))?\/(.+)/;

validate.validators.validSentryDsn = (value: string | undefined | null, options: ValidSentryDsnOptions) => {
  if ((options.allowEmpty && isNil(value)) || value === "") {
    return;
  }

  if (!value) {
    return options.message ?? "must be a valid Sentry DSN";
  }

  if (!DSN_REGEX.test(value)) {
    return options.message ?? "must be a valid Sentry DSN";
  }
};
